import type { Questionnaire, QuestionnaireItem } from '@medplum/fhirtypes';
import {
  ABSENCE_REASON_PROTOTYPE_LINK_ID,
  QUESTIONNAIRE_CANONICAL_URL_PREFIX,
  UNIFY_NOTE_TEMPLATE_IDS,
} from '@unify/fhir-model/constants';

// ---------------------------------------------------------------------------
// UI rendering hint extension
// ---------------------------------------------------------------------------

/**
 * Extension URL used to hint the UI renderer on how to display a choice item.
 * Uses the standard FHIR Questionnaire item-control extension.
 *
 * Values we support:
 *  - "drop-down"   → render as <Select />
 *  - "check-box"   → render as single-column <Checkbox.Group />  (also set `repeats: true`)
 *  - "check-box-grid" → render as 3-column checkbox grid (default for multi-choice)
 *  - "radio-button" → render as <Radio.Group />     (default for choice)
 *  - "rich-text"   → render <RichTextEditor /> instead of plain <Textarea />
 */
export const ITEM_CONTROL_EXT_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl';

/** Helper: get the item-control code from a QuestionnaireItem. */
export function getItemControl(item: QuestionnaireItem): string | undefined {
  const ext = item.extension?.find((e) => e.url === ITEM_CONTROL_EXT_URL);
  return ext?.valueCodeableConcept?.coding?.[0]?.code;
}

/** Helper: build the item-control extension value. */
function itemControl(code: string) {
  return {
    url: ITEM_CONTROL_EXT_URL,
    valueCodeableConcept: {
      coding: [
        {
          system: 'http://hl7.org/fhir/questionnaire-item-control',
          code,
        },
      ],
    },
  };
}

/** Shorthand to build an answerOption with valueString. */
function opt(value: string) {
  return { valueString: value };
}

// ---------------------------------------------------------------------------
// Template: Meridian Counseling Telehealth Progress Note
// ---------------------------------------------------------------------------

const PROGRESS_NOTE_ITEMS: QuestionnaireItem[] = [
  {
    linkId: 'presenting-focus',
    text: 'Presenting focus / Chief concern for this session',
    type: 'text',
    required: true,
    extension: [itemControl('rich-text')],
  },

  // --- MSE Observations ---
  {
    linkId: 'mse-observations',
    text: 'MSE Observations:',
    type: 'group',
    item: [
      {
        linkId: 'mse-affect',
        text: 'Affect',
        type: 'choice',
        required: true,
        extension: [itemControl('drop-down')],
        answerOption: [
          opt('Full Range'),
          opt('Constricted'),
          opt('Flat'),
          opt('Blunted'),
          opt('Labile'),
          opt('Appropriate'),
          opt('Inappropriate'),
        ],
      },
      {
        linkId: 'mse-cognitive',
        text: 'Cognitive Functioning',
        type: 'choice',
        required: true,
        extension: [itemControl('drop-down')],
        answerOption: [
          opt('Intact'),
          opt('Impaired'),
          opt('Disoriented'),
          opt('Alert and Oriented'),
          opt('Confused'),
        ],
      },
      {
        linkId: 'mse-functional',
        text: 'Functional Status',
        type: 'choice',
        required: true,
        extension: [itemControl('drop-down')],
        answerOption: [
          opt('Normal'),
          opt('Mildly Impaired'),
          opt('Moderately Impaired'),
          opt('Severely Impaired'),
          opt('Independent'),
        ],
      },
      {
        linkId: 'mse-interpersonal',
        text: 'Interpersonal',
        type: 'choice',
        required: true,
        extension: [itemControl('drop-down')],
        answerOption: [
          opt('Cooperative'),
          opt('Guarded'),
          opt('Defensive'),
          opt('Hostile'),
          opt('Withdrawn'),
          opt('Engaged'),
        ],
      },
      {
        linkId: 'mse-mood',
        text: 'Mood',
        type: 'choice',
        required: true,
        extension: [itemControl('drop-down')],
        answerOption: [
          opt('Euthymic'),
          opt('Depressed'),
          opt('Anxious'),
          opt('Irritable'),
          opt('Euphoric'),
          opt('Angry'),
          opt('Fearful'),
        ],
      },
    ],
  },

  // --- Narrative sections ---
  {
    linkId: 'session-content',
    text: 'Content or topics discussed specific to this session (please describe):',
    type: 'text',
    required: true,
    extension: [itemControl('rich-text')],
  },
  {
    linkId: 'interventions-utilized',
    text: 'Interventions Utilized:',
    type: 'choice',
    required: true,
    repeats: true,
    extension: [itemControl('check-box')],
    answerOption: [
      opt('Assertive Communication'),
      opt('Behavioral Activation'),
      opt('Brain Spotting'),
      opt('CBT Interventions and Skills'),
      opt('DBT Interventions and Skills'),
      opt('EFT Couples Interventions'),
      opt('EMDR'),
      opt('Empty Chair Technique'),
      opt('Exploration of Coping Skills'),
      opt('Exploration of Emotions'),
      opt('Exploration of Relationships'),
      opt('Explored Values'),
      opt('Guided Imagery'),
      opt('Inner Child Work'),
      opt('Interactive Feedback'),
      opt('Interpersonal Resolutions'),
      opt('Mindfulness Training'),
      opt('Mirroring'),
      opt('Motivational Interviewing'),
      opt('Parts Work'),
      opt('Psychodynamic Framework'),
      opt('Psychoeducation'),
      opt('Reflective Listening'),
      opt('Role Playing'),
      opt('Safety Planning'),
      opt('Solution-Focused Interventions'),
      opt('Somatic or Body Focused Work'),
      opt('Transference or Countertransference Exploration'),
      opt('Other'),
    ],
  },
  {
    linkId: 'client-response-interventions',
    text: "Client's response to interventions this session (please describe):",
    type: 'text',
    required: true,
    extension: [itemControl('rich-text')],
  },
  {
    linkId: 'client-progress-goals',
    text: "Client's progress toward treatment plan goals (please describe):",
    type: 'text',
    required: true,
    extension: [itemControl('rich-text')],
  },
  {
    linkId: 'clinical-impression',
    text: 'Clinical impression / Assessment',
    type: 'text',
    required: true,
    extension: [itemControl('rich-text')],
  },
  {
    linkId: 'safety-screening',
    text: 'Client denies suicidal and homicidal ideation today?',
    type: 'choice',
    required: true,
    answerOption: [
      opt('Yes, denies SI/HI'),
      opt('No – see Risk Factors and documentation below'),
      opt('N/A'),
    ],
  },

  // --- Risk & medication ---
  {
    linkId: 'risk-factors',
    text: 'Risk Factors:',
    type: 'choice',
    repeats: true,
    extension: [itemControl('check-box')],
    answerOption: [
      opt('None'),
      opt('Suicidal Ideation'),
      opt('Homicidal Ideation'),
      opt('Child or Elder Abuse'),
      opt('Other'),
    ],
  },
  {
    linkId: 'therapist-consulted-risk',
    text: 'The therapist consulted with another provider or supervisor regarding identified risk factors:',
    type: 'choice',
    answerOption: [opt('Yes'), opt('No'), opt('N/A')],
  },
  {
    linkId: 'medication-changes',
    text: 'Medication Changes:',
    type: 'choice',
    answerOption: [opt('No changes'), opt('Changes Reported'), opt('N/A')],
  },
  {
    linkId: 'recommendations-homework',
    text: 'Recommendations, plan, or homework assigned before the next session:',
    type: 'text',
    extension: [itemControl('rich-text')],
  },
];

// ---------------------------------------------------------------------------
// Questionnaire resources
// ---------------------------------------------------------------------------

export const PROGRESS_NOTE_QUESTIONNAIRE: Questionnaire = {
  resourceType: 'Questionnaire',
  /** Client slug; server Questionnaire uses pilot UUID — save path resolves via `url` (see pilot seed `buildNoteTemplates`). */
  id: 'meridian-telehealth-progress',
  name: 'meridian-telehealth-progress',
  url: 'https://unify.health/fhir/Questionnaire/meridian-telehealth-progress',
  title: 'Meridian Counseling Telehealth Progress Note',
  status: 'active',
  item: PROGRESS_NOTE_ITEMS,
};

export const LATE_CANCEL_OR_NO_SHOW_NOTE_QUESTIONNAIRE: Questionnaire = {
  resourceType: 'Questionnaire',
  id: UNIFY_NOTE_TEMPLATE_IDS.lateCancelOrNoShow,
  name: UNIFY_NOTE_TEMPLATE_IDS.lateCancelOrNoShow,
  url: `${QUESTIONNAIRE_CANONICAL_URL_PREFIX}${UNIFY_NOTE_TEMPLATE_IDS.lateCancelOrNoShow}`,
  title: 'Late Cancellation or No Show Note',
  status: 'active',
  identifier: [
    {
      system: 'https://unify.health/fhir/questionnaire-template',
      value: UNIFY_NOTE_TEMPLATE_IDS.lateCancelOrNoShow,
    },
  ],
  item: [
    {
      linkId: ABSENCE_REASON_PROTOTYPE_LINK_ID,
      text: 'Absence reason',
      type: 'group',
      item: [
        {
          linkId: 'absence-reason',
          text: 'Why was this session missed?',
          type: 'text',
          required: true,
        },
      ],
    },
  ],
};

/** @deprecated Use {@link LATE_CANCEL_OR_NO_SHOW_NOTE_QUESTIONNAIRE}. */
export const LATE_CANCEL_SESSION_NOTE_QUESTIONNAIRE = LATE_CANCEL_OR_NO_SHOW_NOTE_QUESTIONNAIRE;
/** @deprecated Use {@link LATE_CANCEL_OR_NO_SHOW_NOTE_QUESTIONNAIRE}. */
export const NO_SHOW_SESSION_NOTE_QUESTIONNAIRE = LATE_CANCEL_OR_NO_SHOW_NOTE_QUESTIONNAIRE;

export type OutcomeNoteTemplateKind = 'late-cancel' | 'no-show' | 'mixed';

export const OUTCOME_NOTE_TEMPLATE_IDS: Record<OutcomeNoteTemplateKind, string> = {
  'late-cancel': UNIFY_NOTE_TEMPLATE_IDS.lateCancelOrNoShow,
  'no-show': UNIFY_NOTE_TEMPLATE_IDS.lateCancelOrNoShow,
  mixed: UNIFY_NOTE_TEMPLATE_IDS.lateCancelOrNoShow,
};

export function isOutcomeSpecificNoteQuestionnaire(questionnaire: Questionnaire | null | undefined): boolean {
  if (!questionnaire) return false;
  const ids = new Set<string>([
    ...Object.values(OUTCOME_NOTE_TEMPLATE_IDS),
    'late-cancel-session-note',
    'no-show-session-note',
  ]);
  if (questionnaire.id && ids.has(questionnaire.id)) return true;
  const slug = questionnaire.url?.match(/\/Questionnaire\/([^/?#]+)/)?.[1];
  return slug != null && ids.has(slug);
}
